import { cloneElement, useEffect, useMemo, useState } from "react";
import { Head, router } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import Drawer from "@/components/drawer";
import { useConfirmDialog } from "@/components/confirm-dialog-provider";
import InvitationDrawerForm from "@/pages/invitations/invitation-drawer-form";
import UserEditDrawer from "@/pages/manage/users/user-edit-drawer";
import { invitation_path, manage_invitations_path, manage_user_path, manage_users_path } from "@/routes";

function formatDate(value) {
  if (!value) return "Aldrig";

  return new Intl.DateTimeFormat("da-DK", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function roleLabel(role = "") {
  if (!role) return "Ingen rolle";

  return role.replace(/Role$/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

function ManageUsersIndex({ users = [], invitations = [], onCreateInvitation = null, onEditUser = null }) {
  const { confirm } = useConfirmDialog();

  const removeInvitation = async (invitation) => {
    const confirmed = await confirm({
      title: "Fjern invitation",
      description: `Vil du fjerne invitationen til ${invitation.email}?`,
      confirmLabel: "Fjern",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(invitation_path(invitation.token), {
      preserveScroll: true,
      data: { return_to: "manage_users" }
    });
  };

  const deleteUser = async (user) => {
    const confirmed = await confirm({
      title: "Slet bruger",
      description: `Vil du slette ${user.full_name} og fjerne alle tilknyttede medlemskaber?`,
      confirmLabel: "Slet",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(manage_user_path(user.id), {
      preserveScroll: true
    });
  };

  return (
    <>
      <Head title="Alle brugere" />

      <section className="workspace-content">
        <section className="card">
          <div className="card-body">
            <div className="workspace-section__eyebrow">Administration</div>
            <h2 className="workspace-section__title">Alle brugere</h2>
            <p className="workspace-admin-copy">
              Se alle brugere på tværs af organisationer og få et samlet overblik over aktive medlemskaber og seneste login.
            </p>

            <div className="workspace-admin-table mt-4">
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Navn</th>
                      <th>E-mail</th>
                      <th>Aktiv organisation</th>
                      <th>Organisationer</th>
                      <th>Seneste login</th>
                      <th className="text-end">Handling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-muted py-4">
                          Der er ingen brugere endnu.
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="workspace-admin-name">
                              {user.full_name}
                              {user.current_user ? <span className="workspace-chip">Dig</span> : null}
                            </div>
                          </td>
                          <td>{user.email}</td>
                          <td>
                            <div>{user.active_organization_name || "Ingen aktiv organisation"}</div>
                            {user.active_role ? (
                              <div className="text-muted">{roleLabel(user.active_role)}</div>
                            ) : null}
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              {user.memberships.map((membership) => (
                                <span key={`${user.id}-${membership.organization_id}`} className="workspace-chip">
                                  {membership.organization_name}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="text-muted">{formatDate(user.last_sign_in_at)}</td>
                          <td className="text-end">
                            <div className="d-inline-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => onEditUser?.(user.id)}
                              >
                                Redigér
                              </button>
                              {user.can_destroy ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => deleteUser(user)}
                                >
                                  Slet
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <section className="workspace-section">
          <section className="card">
            <div className="card-body">
              <div className="workspace-section__eyebrow">Invitationer</div>
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div>
                  <h2 className="workspace-section__title">Afventende invitationer</h2>
                  <p className="workspace-admin-copy mb-0">
                    Globale invitationer der endnu ikke er accepteret.
                  </p>
                </div>
                <span className="workspace-chip">{invitations.length}</span>
              </div>

              <div className="workspace-admin-table mt-4">
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>E-mail</th>
                        <th>Organisation</th>
                        <th>Rolle</th>
                        <th>Sendt</th>
                        <th className="text-end">Handling</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-muted py-4">
                            Ingen afventende invitationer.
                          </td>
                        </tr>
                      ) : (
                        invitations.map((invitation) => (
                          <tr key={invitation.id}>
                            <td>{invitation.email}</td>
                            <td>{invitation.organization_name}</td>
                            <td>
                              <span className="workspace-chip">{roleLabel(invitation.role)}</span>
                            </td>
                            <td className="text-muted">{formatDate(invitation.created_at)}</td>
                            <td className="text-end">
                              {invitation.can_destroy ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => removeInvitation(invitation)}
                                >
                                  Fjern
                                </button>
                              ) : null}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </section>
      </section>
    </>
  );
}

function ManageUsersIndexLayout({ page }) {
  const {
    current_tenant: currentTenant,
    organizations = [],
    roles = [],
    new_invitation: newInvitation = null,
    create_errors: createErrors = [],
    show_create_invitation_drawer: showCreateInvitationDrawer = false,
    edit_user: editUser = null,
    edit_errors: editErrors = [],
    show_edit_user_drawer: showEditUserDrawer = false,
    users = []
  } = page.props;
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(showCreateInvitationDrawer);
  const [activeEditUserId, setActiveEditUserId] = useState(editUser?.id || null);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(Boolean(showEditUserDrawer && editUser?.id));
  const [isCreateDrawerSubmitting, setIsCreateDrawerSubmitting] = useState(false);
  const [isEditDrawerSubmitting, setIsEditDrawerSubmitting] = useState(false);
  const activeEditUser = useMemo(
    () => {
      if (editUser?.id === activeEditUserId) return editUser;

      return users.find((user) => user.id === activeEditUserId) || null;
    },
    [activeEditUserId, editUser, users]
  );

  useEffect(() => {
    setIsCreateDrawerOpen(showCreateInvitationDrawer);
  }, [showCreateInvitationDrawer]);

  useEffect(() => {
    setActiveEditUserId(showEditUserDrawer ? editUser?.id || null : null);
  }, [editUser, showEditUserDrawer]);

  useEffect(() => {
    if (showEditUserDrawer && editUser?.id) {
      setIsEditDrawerOpen(true);
    }
  }, [editUser, showEditUserDrawer]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const targetUrl = (Boolean(activeEditUser) && isEditDrawerOpen)
      ? `${manage_users_path()}?edit_user=${activeEditUser.id}`
      : manage_users_path();

    window.history.replaceState(window.history.state, "", targetUrl);
  }, [activeEditUser, isEditDrawerOpen]);

  return (
    <AuthLayout
      header={{
        breadcrumbs: [
          { label: "Administration", href: manage_users_path() },
          { label: "Alle brugere" }
        ],
        title: "Alle brugere",
        actions: [
          {
            onClick: () => setIsCreateDrawerOpen(true),
            label: "Opret",
            variant: "primary"
          }
        ]
      }}
    >
      {cloneElement(page, {
        onCreateInvitation: () => setIsCreateDrawerOpen(true),
        onEditUser: (userId) => {
          setActiveEditUserId(userId);
          setIsEditDrawerOpen(true);
        }
      })}

      <Drawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        title="Opret bruger"
        size="md"
        closeDisabled={isCreateDrawerSubmitting}
      >
        <InvitationDrawerForm
          submitPath={manage_invitations_path()}
          invitation={newInvitation}
          errors={createErrors}
          currentTenant={currentTenant}
          organizations={organizations}
          roles={roles}
          onSuccess={() => setIsCreateDrawerOpen(false)}
          onProcessingChange={setIsCreateDrawerSubmitting}
        />
      </Drawer>

      <Drawer
        isOpen={Boolean(activeEditUser) && isEditDrawerOpen}
        onClose={() => {
          setIsEditDrawerOpen(false);
          setActiveEditUserId(null);
        }}
        title={activeEditUser ? `Redigér ${activeEditUser.full_name}` : "Redigér bruger"}
        size="md"
        closeDisabled={isEditDrawerSubmitting}
      >
        {activeEditUser ? (
          <UserEditDrawer
            user={activeEditUser}
            organizations={organizations}
            errors={showEditUserDrawer && editUser?.id === activeEditUser.id ? editErrors : []}
            onSuccess={() => {
              setIsEditDrawerOpen(false);
              setActiveEditUserId(null);
            }}
            onProcessingChange={setIsEditDrawerSubmitting}
          />
        ) : null}
      </Drawer>
    </AuthLayout>
  );
}

ManageUsersIndex.layout = (page) => <ManageUsersIndexLayout page={page} />;

export default ManageUsersIndex;
